import { shallowRef } from "vue"

// App-wide image crop step for avatar uploads (profile, group, bot): `cropImage(file)` opens the
// crop UI (`ImageCropperHost.vue`, mounted once in app.vue — a Dialog on desktop, a Drawer on
// mobile) and resolves with the cropped square image, or `null` if the user cancelled. Callers
// upload the returned file instead of the original, so the backend only ever gets the crop.
//
// Module-level singleton (same pattern as `useChatStyle`/`useSharedEmojiPicker`): one host, one
// pending request at a time.

export interface CropRequest {
  file: File
  /** Object URL of `file`, owned by the request — revoked once it settles. */
  src: string
  resolve: (result: File | null) => void
}

const request = shallowRef<CropRequest | null>(null)

function settle(result: File | null) {
  const current = request.value
  if (!current) return
  request.value = null
  URL.revokeObjectURL(current.src)
  current.resolve(result)
}

export function useImageCropper() {
  function cropImage(file: File): Promise<File | null> {
    // A new crop replaces one still open (e.g. a second file picked): the old one counts as cancelled.
    settle(null)
    return new Promise((resolve) => {
      request.value = { file, src: URL.createObjectURL(file), resolve }
    })
  }

  return {
    request,
    cropImage,
    /** For the host: finish the current request with the cropped file, or `null` to cancel. */
    finishCrop: settle,
  }
}
